<script lang="ts" setup>
import { PropType, computed, onMounted } from 'vue'
import { UICheckbox, UITextSmRegular, UITooltip } from '@gohighlevel/ghl-ui'
import {
  CheckBoxElementOptions,
  GroupFields,
  GroupFieldsOptions,
  IElement,
  IRecipient,
  MovableComponentEvents,
} from '../../types'
import { PAGE_SECTIONS } from '../../const'
import { ELEMENTS_META } from '../../const'
import { MovableComponentWrapper } from '../MovableComponentWrapper'
import { CommonTooltip } from '../Tooltip'
import { PlusSquareIcon } from '@gohighlevel/ghl-icons/24/outline'
import { getSignatureElementColorStyles } from '../../utils'

const props = defineProps({
  element: Object as PropType<IElement<CheckBoxElementOptions>>,
  isActive: {
    type: Boolean,
    default: false,
  },
  snapContainer: {
    type: String,
  },
  boundContainer: {
    type: String,
  },
  storeMap: {
    type: Object,
    required: true,
  },
  compositionMap: {
    type: Object,
    required: true,
  },
  pageId: {
    type: String,
    required: false,
  },
  pageIndex: {
    type: String,
    required: false,
  },
})

const Asterisk = '*'
const isActiveElement = computed(() => props.isActive)
const builderStore = props.storeMap.useBuilderStore()
const { setSelectoGroupTargets } = props.compositionMap.useGroups()
const store = props.storeMap.useAppStore()
const emits = defineEmits([
  MovableComponentEvents.ON_ACTIVE,
  MovableComponentEvents.ON_CLONE_AND_ADD_TO_GROUP,
  MovableComponentEvents.ON_CLICK_GROUP,
])
const { updateStyles, updateOption } = props.compositionMap.useElementOption()

const elementGroup = computed(() => {
  return builderStore.getElementGroup(props.element?.id)
})

const recipient = computed(() => {
  const recipients = store.document?.recipients || []
  return (
    recipients.find(
      ({ id }) => id === props.element?.component?.options?.recipient
    ) || ({} as IRecipient)
  )
})

const recipientName = computed(() => {
  const { firstName, lastName } = recipient.value
  return firstName ? `${firstName} ${lastName || ''}` : ''
})

const onDrag = (styles: any) => {
  updateStyles('position', styles, props.element?.id)
  updateOption('isGhost', true, props.element?.id)
  builderStore.updateActiveElement(props.element)
}

const onScale = ({ scaleX, scaleY }: { scaleX: number; scaleY: number }) => {
  updateStyles('scale', { scaleX, scaleY }, props.element?.id)
}
const onResize = ({ height, width }: { height: number; width: number }) => {
  updateStyles('dimensions', { height, width }, props.element?.id)
}
const onActive = (e: any, isActive: boolean) => {
  emits(MovableComponentEvents.ON_ACTIVE, e, isActive)
}
const position = computed(() => {
  const pos = props.element?.responsiveStyles.large.position
  return pos || { top: 0, left: 0, bottom: 0 }
})
const scale = computed(() => {
  const scaleObj = props.element?.responsiveStyles.large.scale
  return scaleObj || { scaleX: 1, scaleY: 1 }
})
const align = computed(() => props.element?.responsiveStyles.large.align)
const dimensions = computed(() => {
  const dimensionsObj = props.element?.responsiveStyles.large.dimensions
  if (dimensionsObj?.height && dimensionsObj?.width) {
    return {
      height: dimensionsObj.height + 'px',
      width: dimensionsObj.width + 'px',
    }
  } else {
    return { height: 'auto', width: 'auto' }
  }
})

const colorStyles = computed(() => {
  const dimensionsObj = dimensions.value

  return {
    ...recipientColorStyles.value,
    ...styles.value,
    outline: `1px solid ${recipientColorStyles.value.borderColor}`,
    borderRadius: '6px',
    color: recipientColorStyles.value.placeholderColor,
    'box-shadow': 'none !important',
    overflow: 'hidden',
    padding: '5px',
    ...(dimensionsObj
      ? { height: `${dimensionsObj.height}`, width: `${dimensionsObj.width}` }
      : {}),
  }
})

const setActiveGroup = () => {
  const group = builderStore.getElementGroup(props.element?.id)
  if (group) {
    builderStore.setActiveGroup(group?.id)
    setSelectoGroupTargets()
  }
}

function clone(e: MouseEvent) {
  e.stopImmediatePropagation()
  builderStore.cloneElement(props?.element?.id as string)
}

function del(e: any) {
  e.stopPropagation()
  const elementId = props?.element?.id as string
  if (!elementId) return

  // Check if element is in header/footer section BEFORE deleting
  const section = builderStore.getElementSection(elementId)
  if (
    section &&
    (section === PAGE_SECTIONS.HEADERS || section === PAGE_SECTIONS.FOOTERS)
  ) {
    // Delete from section (this handles propagation across all pages)
    builderStore.deleteElementInSection(elementId, section)
  } else {
    // Delete from content area
    builderStore.deleteElement(elementId)
  }

  // Clear active element if it was the deleted element
  if (builderStore.activeElementId === elementId) {
    builderStore.updateActiveElement(null)
  }

  builderStore.syncRecipient()
}

onMounted(() => {
  builderStore.syncRecipient()
})
const createGroup = (event: MouseEvent) => {
  event.stopPropagation()
  emits(MovableComponentEvents.ON_CLONE_AND_ADD_TO_GROUP, props?.element?.id)
}

const onClickGroup = (
  group: GroupFields<GroupFieldsOptions>,
  elementId: string
) => {
  emits(MovableComponentEvents.ON_CLICK_GROUP, group, elementId)
}

const recipientColorStyles = computed(() => {
  return getSignatureElementColorStyles(recipient.value)
})

const styles = computed(() => {
  const stylesObj: Record<string, string> = {}
  if (scale.value) {
    stylesObj[
      'transform'
    ] = `scale(${scale.value.scaleX}, ${scale.value.scaleY})`
  }
  if (position.value) {
    stylesObj['top'] = `${position.value.top}px`
    stylesObj['left'] = `${position.value.left}px`
    stylesObj['zIndex'] = '100'
  }

  if (dimensions.value) {
    if (dimensions?.value.height !== 'auto') {
      stylesObj['height'] = `${dimensions.value.height}px`
    }
    if (dimensions?.value.width !== 'auto') {
      stylesObj['width'] = `${dimensions.value.width}px`
    }
  }
  return stylesObj
})

const isChecked = computed(() => {
  const text = props.element?.component.options.text
  if (text !== undefined && text !== null && text !== '') {
    return text === 'true'
  }
  return props.element?.component.options.preChecked || false
})

const updateCheckbox = (val: boolean) => {
  if (!elementGroup.value) {
    updateOption(ELEMENTS_META.PRE_CHECKED, val, props.element?.id)
    updateOption(ELEMENTS_META.TEXT, val.toString(), props.element?.id)
  }
}
const onDragEnd = (options: any) => {
  builderStore.moveFloatingElement(props.element?.id as string, options)
  onDrag({
    top: options.styles.top,
    left: options.styles.left,
    bottom: options.styles.bottom,
    right: options.styles.right,
    height: options.styles.height,
    width: options.styles.width,
    preferBottom: options.styles.preferBottom,
    preferRight: options.styles.preferRight,
  })
  setActiveGroup()
  if (builderStore.getElementGroup(props.element?.id)) {
    builderStore.updateActiveElement(null)
  }
}
const onClickElement = (e: MouseEvent) => {
  e.stopPropagation()
  setActiveGroup()
}
const onDragStart = () => {
  builderStore.updateActiveElement(props.element)
}
</script>

<template>
  <MovableComponentWrapper
    :page-index="props.pageIndex"
    :page-id="props.pageId"
    :key="`moveable-element-${props.element?.id}`"
    :id="(props.element?.id as string)"
    @on-drag="onDrag"
    @on-scale="onScale"
    @on-active="onActive"
    @on-resize="onResize"
    @on-drag-end="onDragEnd"
    :is-active-element="isActiveElement"
    :is-editable="builderStore.isDocumentEditable"
    :position="position"
    :scale="scale"
    :align="align"
    :dimensions="props.element?.responsiveStyles.large.dimensions"
    :is-ghost="props.element?.component.options.isGhost"
    :snap-container="props.snapContainer"
    :bound-container="props.boundContainer"
    :is-drag-element="props.element?.component.isDraggable"
    :resizable="true"
    :keep-ratio="true"
    :render-directions="['se']"
    :color-override="recipientColorStyles.placeholderColor"
    :inactive-color-override="recipientColorStyles.borderColor"
    :is-selecto-field="true"
    :element-group="elementGroup"
    @on-click-group="onClickGroup"
    :min-height="20"
    :max-height="50"
    @on-click-element="onClickElement"
    @on-drag-start="onDragStart"
    :enable-active-event-on-drag="false"
  >
    <template #component>
      <span
        v-if="!elementGroup && element?.component.options.required"
        class="option--required"
        >{{ Asterisk }}</span
      >
      <div class="el-wrapper checkbox-field">
        <UICheckbox
          :checked="isChecked"
          :style="colorStyles"
          :key="props.element?.id"
          :id="`floating_checkbox_field-${props.element?.id}`"
          :type="'default'"
          @update:checked="updateCheckbox"
        />
      </div>
    </template>
    <template #tooltip v-if="recipientName">
      <UITextSmRegular
        class="capitalize font-semibold py-1 px-4 text-xs"
        :style="{ color: recipientColorStyles.placeholderColor }"
        >{{ recipientName }}</UITextSmRegular
      >
    </template>
    <template #popup-options>
      <CommonTooltip @on-clone="clone" @on-delete="del" />
    </template>
    <template #group-options>
      <UITooltip
        :key="`add-to-checkbox-element-${props.element?.id}`"
        v-if="!elementGroup"
        :placement="'bottom'"
      >
        <template #trigger>
          <div
            class="text-primary-700 cursor-pointer flex"
            @click="createGroup"
          >
            <PlusSquareIcon class="w-6 h-6 p-1" />
          </div>
        </template>
        {{ $t('common.addCheckboxToGroup') }}
      </UITooltip>
    </template>
  </MovableComponentWrapper>
</template>

<style lang="scss">
.checkbox-field {
  width: v-bind('dimensions.width') !important;
  height: v-bind('dimensions.height') !important;
  .n-checkbox {
    padding: 0 !important;
    width: v-bind('dimensions.width') !important;
    height: v-bind('dimensions.height') !important;
    --n-size: v-bind('dimensions.width') !important;
  }
}
</style>
